import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check, FileText, HeartHandshake, LogOut, Play, Repeat, Search, TimerReset, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, EmptyState, MetricCard, PageHeader, Panel, PanelHeader, SearchField, StatusBadge } from "./ui";
import { dateTimeBR, dayBR, FlagChips, PatientAvatar, PriorityNote } from "./MeetingBits";
import { AllocationPanel } from "./MeetingAllocation";
import { AgeChip, wardLabel, wardScope } from "@/lib/patient";
import { SortSelect } from "./PatientSort";
import { useScrollIntoViewWhen } from "@/lib/scrollTo";

const ParticipantsPicker = ({ options, selected, onToggle }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (options || []).filter((o) => o.name.toLowerCase().includes(term));
  }, [options, query]);

  return (
    <div className="space-y-3">
      <label className="relative flex items-center">
        <Search size={16} className="absolute left-3.5 text-ink-400" aria-hidden="true" />
        <input
          data-testid="meeting-participants-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar funcionário pelo nome"
          className="h-11 w-full rounded-xl border border-border bg-white pl-10 pr-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
        />
      </label>

      {selected.length ? (
        <div data-testid="meeting-participants-chips" className="flex flex-wrap gap-2">
          {selected.map((s) => (
            <span
              key={s.id}
              data-testid={`meeting-participant-chip-${s.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-sage-600 px-3 py-1.5 text-[13px] font-semibold text-white"
            >
              {s.name}
              <button
                type="button"
                aria-label={`Remover ${s.name}`}
                data-testid={`meeting-participant-remove-${s.id}`}
                onClick={() => onToggle(s)}
                className="grid h-5 w-5 place-items-center rounded-full bg-white/20 hover:bg-white/30"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[13.5px] text-ink-500">Nenhum participante selecionado ainda.</p>
      )}

      <ul data-testid="meeting-participants-list" className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-border p-1.5">
        {filtered.map((o) => {
          const active = selected.some((s) => s.id === o.id);
          return (
            <li key={o.id}>
              <button
                type="button"
                data-testid={`meeting-participant-option-${o.id}`}
                onClick={() => onToggle(o)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-3.5 py-2.5 text-left text-[14.5px] transition-colors ${
                  active ? "bg-sage-50 text-ink-900" : "text-ink-700 hover:bg-sand"
                }`}
              >
                <span>
                  {o.name}
                  <span className="ml-2 text-[12.5px] text-ink-500">{o.staff_type_label}</span>
                </span>
                {active ? <Check size={16} className="text-sage-700" aria-hidden="true" /> : null}
              </button>
            </li>
          );
        })}
        {filtered.length === 0 ? (
          <li className="px-3 py-2.5 text-[13.5px] text-ink-500">Nenhum funcionário ativo com esse nome.</li>
        ) : null}
      </ul>
    </div>
  );
};

const QueueCard = ({ item, meetingId, onOpen }) => (
  <li
    data-testid={`meetings-queue-item-${item.patient_id}`}
    className={`flex flex-wrap items-start justify-between gap-4 rounded-xl border px-4 py-3.5 ${
      item.priority ? "border-danger/45 bg-danger/[0.05]" : "border-border bg-white"
    }`}
  >
    <div className="flex min-w-[240px] flex-1 gap-3.5">
      <PatientAvatar
        name={item.full_name}
        initials={item.initials}
        photoUrl={item.photo_thumb_url}
        testId={`meetings-queue-avatar-${item.patient_id}`}
      />
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[15.5px] font-semibold text-ink-900 break-words">{item.full_name}</p>
          <AgeChip patient={item} testId={`meetings-queue-age-${item.patient_id}`} />
          <span data-testid={`meetings-queue-ward-${item.patient_id}`} className="text-[13px] text-ink-500">
            Ala: {wardLabel(item)}
          </span>
        </div>
        <PriorityNote item={item} testId={`meetings-queue-priority-${item.patient_id}`} />
        <p className="text-[13.5px] text-ink-600">
          Última discussão: {item.last_discussion_at ? dateTimeBR(item.last_discussion_at) : "nunca"} ·{" "}
          {item.never_discussed ? "sem discussão" : `${item.days_without_discussion} dia(s) sem discussão`}
        </p>
        <p className="text-[13.5px] text-ink-600">Alta prevista: {dayBR(item.expected_discharge_date)}</p>
        <p className="text-[13.5px] text-ink-600">
          Conselheiro: {item.care_team?.counselor?.name || "não definido"} · Psicólogo(a):{" "}
          {item.care_team?.psychologist?.name || "não definido"} · Psiquiatra:{" "}
          {item.care_team?.psychiatrist?.name || "não definido"}
        </p>
        <FlagChips flags={item.flags} testId={`meetings-queue-flags-${item.patient_id}`} />
      </div>
    </div>
    <div className="flex flex-col items-end gap-2">
      {meetingId ? (
        <StatusBadge
          tone={item.meeting_status === "discussed" ? "done" : "pending"}
          testId={`meetings-queue-status-${item.patient_id}`}
        >
          {item.meeting_status === "discussed" ? "Discutido" : "Pendente"}
        </StatusBadge>
      ) : null}
      <Action
        variant="secondary"
        size="sm"
        data-testid={`meetings-queue-open-${item.patient_id}`}
        onClick={onOpen}
      >
        {meetingId ? (item.meeting_status === "discussed" ? "Complementar" : "Discutir") : "Abrir paciente"}
      </Action>
    </div>
  </li>
);

export default function Meetings() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const scope = wardScope(user);
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [opening, setOpening] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ward, setWard] = useState(scope.value);
  const [sort, setSort] = useState("name_asc");
  const [queryText, setQueryText] = useState("");
  const [queue, setQueue] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [showAllocation, setShowAllocation] = useState(false);
  const openingRef = useRef(null);
  useScrollIntoViewWhen(openingRef, opening);
  // A fila só existe quando há reunião aberta: sem ela, nada de lista nem de controles de discussão.
  const hasOpenMeeting = Boolean(data?.current);
  const isAdmin = user?.role === "admin";
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [overview, options] = await Promise.all([
        api.get("/portal/meetings/overview", { params: { ward } }),
        api.get("/portal/meetings/staff-options"),
      ]);
      setData(overview.data);
      setStaff(options.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setData({ current: null, totals: {}, history: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  const loadQueue = useCallback(async () => {
    if (!hasOpenMeeting) {
      setQueue(null);
      setMeetingId(null);
      return;
    }
    try {
      const { data: payload } = await api.get("/portal/meetings/queue", {
        params: { ward, sort, q: queryText.trim() || undefined },
      });
      setQueue(payload.items);
      setMeetingId(payload.meeting_id);
    } catch {
      setQueue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward, sort, queryText, hasOpenMeeting]);

  const loadAllocation = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/portal/meetings/allocation", { params: { ward } });
      setAllocation(payload);
    } catch {
      setAllocation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  useEffect(() => {
    load();
    loadAllocation();
  }, [load, loadAllocation]);

  useEffect(() => {
    const timer = setTimeout(loadQueue, 250);
    return () => clearTimeout(timer);
  }, [loadQueue]);

  const toggle = (s) =>
    setSelected((prev) => (prev.some((p) => p.id === s.id) ? prev.filter((p) => p.id !== s.id) : [...prev, s]));

  const openMeeting = async () => {
    if (selected.length === 0) {
      toast.error("Selecione pelo menos um participante para abrir a reunião.");
      return;
    }
    setSaving(true);
    try {
      const { data: meeting } = await api.post("/portal/meetings", {
        participant_ids: selected.map((s) => s.id),
      });
      toast.success("Reunião clínica aberta.");
      navigate(`/portal/reunioes/${meeting.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const deleteMeeting = async (meeting) => {
    const wards = (meeting.wards || []).join(" e ") || "sem ala definida";
    const confirmed = window.confirm(
      `Reunião de ${dateTimeBR(meeting.opened_at)} · Ala: ${wards}\n\n` +
        "Deseja excluir esta reunião clínica? Ela deixará de aparecer no histórico e nos indicadores relacionados."
    );
    if (!confirmed) return;
    setDeletingId(meeting.id);
    try {
      await api.delete(`/portal/meetings/${meeting.id}`);
      toast.success("Reunião clínica excluída.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setDeletingId(null);
  };

  if (!data) return <Loading label="Carregando reuniões clínicas…" />;

  const t = data.totals || {};

  return (
    <div data-testid="portal-meetings-page" className="space-y-7">
      <PageHeader
        kicker="Evolução semanal"
        title="Reuniões Clínicas"
        subtitle="Registro das reuniões de evolução: fila por prioridade, definições por paciente, marcações persistentes e ata gerada automaticamente."
        actions={
          data.current ? (
            <Action
              icon={Play}
              data-testid="meeting-continue-button"
              onClick={() => navigate(`/portal/reunioes/${data.current.id}`)}
            >
              Continuar reunião
            </Action>
          ) : (
            <Action icon={Play} data-testid="meeting-open-button" onClick={() => setOpening(true)}>
              Abrir nova reunião
            </Action>
          )
        }
      />

      {data.current ? (
        <Panel testId="meeting-current-panel" className="border-sage-600/40 bg-sage-50/40">
          <PanelHeader
            label="Reunião em andamento"
            title={`Aberta em ${dateTimeBR(data.current.opened_at)}`}
            hint={`Por ${data.current.opened_by_name || "—"} · ${
              (data.current.participants || []).length
            } participante(s): ${(data.current.participants || []).map((p) => p.name).join(", ")}`}
            actions={
              <Action
                icon={Play}
                size="sm"
                data-testid="meeting-current-continue"
                onClick={() => navigate(`/portal/reunioes/${data.current.id}`)}
              >
                Continuar reunião
              </Action>
            }
          />
        </Panel>
      ) : null}

      <Panel testId="meetings-filters-panel" className="space-y-4">
        <PanelHeader
          label="Filtros"
          title="Pacientes considerados nesta tela"
          hint="Somente pacientes ativos da ala autorizada. Métricas, fila e alocação seguem o filtro escolhido."
          actions={
            scope.locked ? (
              <span
                data-testid="meetings-ward-scope"
                className="inline-flex h-11 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
              >
                {scope.label}
              </span>
            ) : (
              <select
                data-testid="meetings-filter-ward"
                aria-label="Ala"
                value={ward}
                onChange={(e) => setWard(e.target.value)}
                className="h-11 w-full min-w-0 max-w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15 sm:w-auto"
              >
                <option value="all">Todas as alas</option>
                <option value="MASCULINA">Masculina</option>
                <option value="FEMININA">Feminina</option>
              </select>
            )
          }
        />
      </Panel>

      <section data-testid="meeting-metrics" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          testId="meeting-metric-active"
          label="Pacientes ativos"
          value={t.active_patients ?? 0}
          hint="Em acompanhamento, ainda não desligados"
          icon={Users}
        />
        <MetricCard
          testId="meeting-metric-pending"
          label="Ainda não discutidos"
          value={t.pending ?? 0}
          hint={t.discussed_scope === "meeting" ? "Pendentes na reunião em andamento" : "Sem discussão nesta semana"}
          icon={TimerReset}
          alert={(t.pending ?? 0) > 0}
        />
        <MetricCard
          testId="meeting-metric-discussed"
          label="Já discutidos"
          value={t.discussed ?? 0}
          hint={t.discussed_scope === "meeting" ? "Concluídos na reunião em andamento" : "Concluídos nesta semana"}
          icon={Check}
        />
        <MetricCard
          testId="meeting-metric-overdue"
          label="Há mais de 14 dias"
          value={t.overdue_patients ?? 0}
          hint="Prioridade para a próxima reunião"
          icon={CalendarClock}
          alert={(t.overdue_patients ?? 0) > 0}
        />
        <MetricCard
          testId="meeting-metric-never"
          label="Nunca discutidos"
          value={t.never_discussed ?? 0}
          hint="Sem nenhuma discussão registrada"
          icon={CalendarClock}
          alert={(t.never_discussed ?? 0) > 0}
        />
        <MetricCard
          testId="meeting-metric-help"
          label="Pedido de ajuda"
          value={t.help_request ?? 0}
          hint="Marcações abertas de pedido de ajuda"
          icon={HeartHandshake}
          alert={(t.help_request ?? 0) > 0}
        />
        <MetricCard
          testId="meeting-metric-resocialization"
          label="Ressocialização em discussão"
          value={t.resocialization ?? 0}
          hint="Marcações abertas de ressocialização"
          icon={Repeat}
        />
        <MetricCard
          testId="meeting-metric-discharge"
          label="Alta em discussão"
          value={t.discharge ?? 0}
          hint="Marcações abertas de alta"
          icon={LogOut}
        />
        <MetricCard
          testId="meeting-metric-extension"
          label="Prorrogação em discussão"
          value={t.extension ?? 0}
          hint="Marcações abertas de prorrogação"
          icon={CalendarClock}
        />
        <MetricCard
          testId="meeting-metric-week"
          label="Discutidos na semana"
          value={t.discussed_this_week ?? 0}
          hint="Discussões concluídas nesta semana"
          icon={Check}
        />
      </section>

      {data.current ? (
      <Panel testId="meetings-queue-panel" className="space-y-4">
        <PanelHeader
          label="Fila"
          title="Pacientes disponíveis para discussão"
          hint={
            meetingId
              ? "Abra o paciente para registrar a discussão na reunião em andamento."
              : "Sem reunião aberta: abra a ficha do paciente para consultar o caso."
          }
          actions={
            <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
              <SearchField
                testId="meetings-queue-search"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Buscar paciente pelo nome"
                className="w-full min-w-0 sm:max-w-xs"
              />
              <SortSelect value={sort} onChange={setSort} includeMeeting testId="meetings-sort-select" />
            </div>
          }
        />
        {queue === null ? (
          <p className="text-[14px] text-ink-500">Carregando fila…</p>
        ) : queue.length === 0 ? (
          <EmptyState
            testId="meetings-queue-empty"
            icon={Users}
            title="Nenhum paciente para os filtros atuais"
            description="Ajuste a ala ou a busca para ver outros pacientes ativos."
          />
        ) : (
          <ul data-testid="meetings-queue-list" className="space-y-3">
            {queue.map((item) => (
              <QueueCard
                key={item.patient_id}
                item={item}
                meetingId={meetingId}
                onOpen={() =>
                  navigate(
                    meetingId
                      ? `/portal/reunioes/${meetingId}/paciente/${item.patient_id}`
                      : `/portal/pacientes/${item.patient_id}`
                  )
                }
              />
            ))}
          </ul>
        )}
      </Panel>
      ) : (
        <EmptyState
          testId="meetings-no-open-meeting"
          icon={Users}
          title="Nenhuma reunião clínica aberta."
          description="A fila de pacientes fica disponível a partir da abertura de uma reunião. O histórico de reuniões encerradas continua acessível abaixo."
        />
      )}

      {allocation && showAllocation ? (
        <AllocationPanel data={allocation} onClose={() => setShowAllocation(false)} backLabel="Ocultar" />
      ) : (
        <Panel testId="meetings-allocation-hidden">
          <PanelHeader
            label="Alocação da equipe"
            title="Carga por profissional"
            hint="Conselheiros, psicólogos(as) e psiquiatras dentro da ala selecionada."
            actions={
              <Action
                variant="secondary"
                size="sm"
                data-testid="meetings-allocation-show"
                onClick={() => setShowAllocation(true)}
              >
                Ver alocação
              </Action>
            }
          />
        </Panel>
      )}

      {opening ? (
        <div ref={openingRef} className="scroll-mt-32">
        <Panel testId="meeting-open-panel" className="space-y-5">
          <PanelHeader
            label="Abertura"
            title="Participantes da reunião"
            hint="Escolha entre os funcionários ativos, de qualquer cargo. Pelo menos um participante é obrigatório."
          />
          <ParticipantsPicker options={staff} selected={selected} onToggle={toggle} />
          <div className="flex flex-wrap gap-2.5">
            <Action icon={Check} data-testid="meeting-open-confirm" disabled={saving || selected.length === 0} onClick={openMeeting}>
              {saving ? "Abrindo…" : "Abrir reunião"}
            </Action>
            <Action
              variant="secondary"
              data-testid="meeting-open-cancel"
              onClick={() => {
                setOpening(false);
                setSelected([]);
              }}
            >
              Cancelar
            </Action>
          </div>
        </Panel>
        </div>
      ) : null}

      <Panel testId="meeting-history-panel" className="space-y-4">
        <PanelHeader
          label="Histórico"
          title="Reuniões encerradas"
          hint="Os registros e a ata permanecem disponíveis para consulta."
        />
        {(data.history || []).length === 0 ? (
          <EmptyState
            testId="meeting-history-empty"
            icon={FileText}
            title="Nenhuma reunião encerrada ainda"
            description="Quando a primeira reunião for encerrada, a ata aparecerá aqui."
          />
        ) : (
          <ul data-testid="meeting-history-list" className="space-y-3">
            {data.history.map((m) => (
              <li
                key={m.id}
                data-testid={`meeting-history-item-${m.id}`}
                className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border px-4 py-3.5"
              >
                <div className="min-w-[240px]">
                  <p className="text-[15px] font-semibold text-ink-900">
                    {dateTimeBR(m.opened_at)} — {dateTimeBR(m.closed_at)}
                  </p>
                  <p className="mt-1 text-[13.5px] text-ink-600">
                    {m.discussed_count} paciente(s) discutido(s) · {(m.participants || []).length} participante(s)
                    {m.rectifications?.length ? ` · ${m.rectifications.length} retificação(ões)` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone="done">Encerrada</StatusBadge>
                  <Action
                    variant="secondary"
                    size="sm"
                    icon={FileText}
                    data-testid={`meeting-history-minutes-${m.id}`}
                    onClick={() => navigate(`/portal/reunioes/${m.id}/ata`)}
                  >
                    Ver ata
                  </Action>
                  <Action
                    variant="secondary"
                    size="sm"
                    data-testid={`meeting-history-open-${m.id}`}
                    onClick={() => navigate(`/portal/reunioes/${m.id}`)}
                  >
                    Ver registros
                  </Action>
                  {isAdmin ? (
                    <Action
                      variant="secondary"
                      size="sm"
                      icon={Trash2}
                      data-testid={`meeting-history-delete-${m.id}`}
                      disabled={deletingId === m.id}
                      onClick={() => deleteMeeting(m)}
                      className="border-danger/50 text-danger hover:bg-danger/10"
                    >
                      {deletingId === m.id ? "Excluindo…" : "Excluir reunião"}
                    </Action>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
